import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
  type QueryClient,
  type UseSuspenseQueryResult,
} from "@tanstack/react-query";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import { axiosClient, useAxios } from "@wayflyer/auth-sdk";
import type {
  BankConnectSchema,
  BankingScreen,
  BankingSettings,
  GroupedBankingSourcesSchema,
} from "@wayflyer/customer-bankconnect";
import { getStageCompletionStatusQueryOptions } from "shared-ui";

import {
  BANK_CONNECT_API,
  BANK_CONNECT_GROUPED_BANKING_SOURCES_API,
  BANK_CONNECT_COVERAGE_ACKNOWLEDGEMENT_API,
  BANK_CONNECT_MOVE_STATEMENT_API,
  BANK_CONNECT_REMOVE_STATEMENT_API,
  BANK_CONNECT_SOURCES_API,
  BANK_CONNECT_SYNC_ACCOUNT_GROUPS_API,
  BANK_CONNECT_UNASSIGN_STATEMENT_API,
  DEFAULT_GROUP_NAME,
} from "../../components/constants";

export type BankConnectScreenProps = { initialScreen: BankingScreen; bankConnectProps: BankConnectSchema };
export const useBankConnectProps = (companySlug: string) => {
  return useSuspenseQuery({
    ...bankConnectScreenPropsOptions(companySlug),
    select: ({ data }): BankConnectScreenProps => {
      // Temporarily hardcode the path that connection + statement summary card "Add connection" button directs to,
      // so we avoid changing BE bankconnect service code
      data.bankConnectProps.bankSettings.bankingScreenPath = `/${companySlug}/funding/application/connections/banking`;
      return { ...data };
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const bankConnectScreenPropsOptions = (companySlug: string) => {
  const fetchUrl = BANK_CONNECT_API.replace(":customerSlug", companySlug);

  const queryFn = () => axiosClient.get<BankConnectScreenProps>(fetchUrl);
  return queryOptions({
    queryKey: [fetchUrl, companySlug],
    queryFn,
    select: ({ data }) => data,
  });
};

// Returns of banking data sources and bank statement group data
export const useBankingSources = (companySlug: string): UseSuspenseQueryResult<BankingSettings> => {
  const client = useAxios();
  const fetchUrl = BANK_CONNECT_SOURCES_API.replace(":customerSlug", companySlug);
  const queryFn = () => client.get<BankingSettings>(fetchUrl);
  return useSuspenseQuery({
    queryKey: [fetchUrl, companySlug],
    queryFn,
    select: ({ data }): BankingSettings => {
      return { ...data };
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

// Catches up any connections/uploads not yet synced into account groups.
export const useSyncAccountGroups = (companySlug: string) => {
  const client = useAxios();
  const postUrl = BANK_CONNECT_SYNC_ACCOUNT_GROUPS_API.replace(":customerSlug", companySlug);
  return useMutation({
    mutationFn: () => client.post(postUrl),
  });
};

// Moves (or first-attaches) an uploaded statement to a different account group, or a new one.
export const useMoveStatementToGroup = (companySlug: string) => {
  const client = useAxios();
  return useMutation({
    mutationFn: ({
      uploadUuid,
      params,
    }: {
      uploadUuid: string;
      params: FundingApplicationSchemas.MoveStatementParams;
    }) => {
      const postUrl = BANK_CONNECT_MOVE_STATEMENT_API.replace(":customerSlug", companySlug).replace(
        ":uploadUuid",
        uploadUuid
      );
      return client.post(postUrl, params);
    },
  });
};

// Detaches an uploaded statement from its account group without deleting it, returning it to the unmatched list.
export const useUnassignStatement = (companySlug: string) => {
  const client = useAxios();
  return useMutation({
    mutationFn: ({
      uploadUuid,
      params,
    }: {
      uploadUuid: string;
      params: FundingApplicationSchemas.UnassignStatementParams;
    }) => {
      const postUrl = BANK_CONNECT_UNASSIGN_STATEMENT_API.replace(":customerSlug", companySlug).replace(
        ":uploadUuid",
        uploadUuid
      );
      return client.post(postUrl, params);
    },
  });
};

export const useSetCoverageAcknowledgement = (companySlug: string) => {
  const client = useAxios();
  return useMutation({
    mutationFn: (params: FundingApplicationSchemas.SetCoverageAcknowledgementParams) =>
      client.post<void>(BANK_CONNECT_COVERAGE_ACKNOWLEDGEMENT_API.replace(":customerSlug", companySlug), params),
  });
};

export const useRemoveStatement = (
  companySlug: string,
  options?: { onSuccess?: () => void; onError?: (error: Error) => void }
) => {
  const client = useAxios();
  return useMutation({
    mutationFn: ({ uploadUuid }: { uploadUuid: string }) => {
      const postUrl = BANK_CONNECT_REMOVE_STATEMENT_API.replace(":customerSlug", companySlug).replace(
        ":uploadUuid",
        uploadUuid
      );
      return client.post(postUrl);
    },
    ...options,
  });
};

// Statements sitting in these states can still transition on the backend at any time, so the
// query below keeps polling until every statement, grouped or not, has settled.
const NON_TERMINAL_EXTRACTION_STATES = new Set(["created", "processing"]);
const GROUPED_BANKING_SOURCES_POLL_INTERVAL_MS = 5 * 1000;

const isNonTerminalExtraction = ({ extractionState }: { extractionState: string | null }): boolean =>
  extractionState !== null && NON_TERMINAL_EXTRACTION_STATES.has(extractionState);

export const groupedBankingSourcesRefetchInterval = (data: GroupedBankingSourcesSchema | undefined): number | false => {
  const hasNonTerminalExtraction =
    (data?.ungroupedStatements ?? []).some(isNonTerminalExtraction) ||
    (data?.accountGroups ?? []).some((group) => group.statementMembers.some(isNonTerminalExtraction));
  return hasNonTerminalExtraction ? GROUPED_BANKING_SOURCES_POLL_INTERVAL_MS : false;
};

export const groupedBankingSourcesQueryOptions = (companySlug: string) => {
  const fetchUrl = BANK_CONNECT_GROUPED_BANKING_SOURCES_API.replace(":customerSlug", companySlug);
  const queryFn = () => axiosClient.get<GroupedBankingSourcesSchema>(fetchUrl);
  return queryOptions({
    queryKey: [fetchUrl, companySlug],
    queryFn,
    select: ({ data }): GroupedBankingSourcesSchema => data,
    refetchInterval: (query) => groupedBankingSourcesRefetchInterval(query.state.data?.data),
  });
};

// Everything that can change after a bank statement is submitted or an account group edited -
// including the workflow tracker's own completion status, so its step icon stays in sync.
export const invalidateBankingSourceQueries = (queryClient: QueryClient, companySlug: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: groupedBankingSourcesQueryOptions(companySlug).queryKey }),
    queryClient.invalidateQueries({
      queryKey: [BANK_CONNECT_SOURCES_API.replace(":customerSlug", companySlug), companySlug],
    }),
    queryClient.invalidateQueries({
      queryKey: [BANK_CONNECT_API.replace(":customerSlug", companySlug), companySlug],
    }),
    queryClient.invalidateQueries({ queryKey: getStageCompletionStatusQueryOptions(companySlug).queryKey }),
  ]);

export interface UnsubmittedUploadGroup {
  group: string;
  uuids: string[];
}

export const getUnsubmittedUploadGroups = (
  groupedBankingSources: GroupedBankingSourcesSchema
): UnsubmittedUploadGroup[] => {
  const claimedIds = new Set<string>();
  const accountGroupSubmissions = groupedBankingSources.accountGroups.flatMap((accountGroup) => {
    const uuids = [
      ...new Set(accountGroup.statementMembers.filter((member) => !member.isSubmitted).map((member) => member.id)),
    ].filter((id) => !claimedIds.has(id));
    uuids.forEach((id) => claimedIds.add(id));
    return uuids.length > 0 ? [{ group: DEFAULT_GROUP_NAME, uuids }] : [];
  });

  const ungroupedSubmissions = groupedBankingSources.ungroupedStatements
    .filter((statement) => !statement.isSubmitted)
    .map((statement) => ({ group: DEFAULT_GROUP_NAME, uuids: [statement.id] }));

  return [...accountGroupSubmissions, ...ungroupedSubmissions];
};
